import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Search, /* Plus, Pencil, Trash2, */ Users, UserCheck, Clock, UserPlus } from 'lucide-react'
// import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import { Pagination } from '../../common/Select/Select'
import {
  fetchCustomers,
  // addCustomer,
  // editCustomer,
  // removeCustomer,
  clearCustomerError,
} from './services/customerSlice'

// const EMPTY_FORM = { first_name: '', last_name: '', email: '', phone: '', password: '' }

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '-'

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'verified', label: 'Verified' },
  { key: 'not_verified', label: 'Not Verified' },
]

export default function Customer() {
  const dispatch = useDispatch()
  const { customers, isLoading, isError, message } = useSelector((s) => s.customer)
  // const { isSaving } = useSelector((s) => s.customer)

  const [search, setSearch] = useState('')
  const [verifyFilter, setVerifyFilter] = useState('all') // all | verified | not_verified

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  /* ---- ADD / EDIT / DELETE state (disabled) ----
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [formErr, setFormErr] = useState('')
  */

  useEffect(() => {
    dispatch(fetchCustomers())
    return () => {
      dispatch(clearCustomerError())
    }
  }, [dispatch])

  // API customer -> UI shape
  const users = useMemo(
    () =>
      (customers || []).map((c) => ({
        id: c.id,
        first_name: c.first_name,
        last_name: c.last_name,
        name: `${c.first_name || ''} ${c.last_name || ''}`.trim(),
        email: c.email,
        phone: c.phone,
        status: c.email_verified_at ? 'active' : 'pending',
        joined: formatDate(c.created_at),
        created_at: c.created_at,
      })),
    [customers]
  )

  const filtered = users.filter((u) => {
    // verified filter
    if (verifyFilter === 'verified' && u.status !== 'active') return false
    if (verifyFilter === 'not_verified' && u.status !== 'pending') return false

    // search
    const q = search.trim().toLowerCase()
    if (!q) return true
    return (
      u.name.toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').includes(q)
    )
  })

  /* ---------------- pagination ---------------- */
  useEffect(() => { setPage(1) }, [search, verifyFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handlePageSizeChange = (size) => { setPageSize(size); setPage(1) }

  /* ---------------- modal handlers (disabled) ----------------
  const openAdd = () => {
    setActive(null)
    setForm(EMPTY_FORM)
    setFormErr('')
    dispatch(clearCustomerError())
    setModalOpen(true)
  }

  const openEdit = (user) => {
    setActive(user)
    setForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '',
    })
    setFormErr('')
    dispatch(clearCustomerError())
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setFormErr('')
    dispatch(clearCustomerError())
  }

  const saveUser = async () => {
    setFormErr('')

    if (!form.first_name.trim() || !form.last_name.trim()) {
      return setFormErr('First name and last name are required.')
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return setFormErr('Enter a valid email address.')
    }
    if (!/^[6-9][0-9]{9}$/.test(form.phone.trim())) {
      return setFormErr('Enter a valid 10-digit phone number.')
    }
    if (!active && form.password.length < 8) {
      return setFormErr('Password must be at least 8 characters.')
    }
    if (active && form.password && form.password.length < 8) {
      return setFormErr('Password must be at least 8 characters.')
    }

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
    }
    if (form.password) payload.password = form.password

    const result = active
      ? await dispatch(editCustomer({ id: active.id, ...payload }))
      : await dispatch(addCustomer(payload))

    if (result.meta.requestStatus === 'fulfilled') {
      closeModal()
    } else {
      setFormErr(result.payload?.message || 'Something went wrong.')
    }
  }

  const confirmDelete = async () => {
    if (!active) return
    await dispatch(removeCustomer(active.id))
    setDeleteOpen(false)
    setActive(null)
  }
  */

  /* ---------------- stats ---------------- */
  const total = users.length
  const verifiedCount = users.filter((u) => u.status === 'active').length
  const pendingCount = users.filter((u) => u.status === 'pending').length
  const now = new Date()
  const newThisMonth = users.filter((u) => {
    if (!u.created_at) return false
    const d = new Date(u.created_at)
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  }).length
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0)

  const tabCounts = { all: total, verified: verifiedCount, not_verified: pendingCount }

  const stats = [
    { label: 'Total Customers', value: total, icon: Users, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', trend: `${total}`, trendColor: '#4F46E5', variant: 'indigo' },
    { label: 'Verified', value: verifiedCount, icon: UserCheck, color: '#06B6D4', colorGlow: 'rgba(6,182,212,0.15)', trend: `${pct(verifiedCount)}%`, trendColor: '#06B6D4', variant: 'cyan' },
    { label: 'Not Verified', value: pendingCount, icon: Clock, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', trend: `${pct(pendingCount)}%`, trendDir: 'neutral', trendColor: '#F59E0B', variant: 'amber' },
    { label: 'New This Month', value: newThisMonth, icon: UserPlus, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', trend: `${pct(newThisMonth)}%`, trendColor: '#10B981', variant: 'green' },
  ]

  return (
    <div className="dash-page page-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
        <h1 className="page-title">Customers</h1>
        {/* <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={14} /> Add Customer
        </button> */}
      </div>

      {/* ---------- stats ---------- */}
      <div className="stats-grid cols-4">
        {stats.map((s) => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span
              style={{
                position: 'absolute', top: 0, right: 0, width: 110, height: 110,
                borderRadius: '50%', background: s.colorGlow, filter: 'blur(30px)',
                transform: 'translate(30%, -30%)', pointerEvents: 'none',
              }}
            />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span
                className={`stat-card-trend${s.trendDir === 'down' ? ' down' : s.trendDir === 'neutral' ? ' neutral' : ''}`}
                style={{ color: s.trendColor, background: `${s.trendColor}15`, border: `1px solid ${s.colorGlow}` }}
              >
                {s.trend}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div
              style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
                background: `linear-gradient(90deg,${s.color}88,transparent)`,
                borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
              }}
            />
          </div>
        ))}
      </div>

      {/* ---------- table ---------- */}
      <div className="card" style={{ padding: 0 }}>
        <div className="cust-toolbar">
          <div className="cust-search">
            <Search size={13} className="cust-search-icon" />
            <input
              className="input"
              style={{ paddingLeft: 32 }}
              placeholder="Search name, email or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* segmented filter (All / Verified / Not Verified) */}
          <div className="seg-filter" role="tablist" aria-label="Filter by verification status">
            {FILTER_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={verifyFilter === t.key}
                className={`seg-btn${verifyFilter === t.key ? ' active' : ''}`}
                onClick={() => setVerifyFilter(t.key)}
              >
                {t.label}
                <span className="seg-count">{tabCounts[t.key]}</span>
              </button>
            ))}
          </div>

          <button className="btn btn-outline btn-sm" onClick={() => dispatch(fetchCustomers())}>
            Refresh
          </button>
        </div>

        {isError && (
          <div style={{ padding: 'var(--space-md) var(--space-lg)', color: '#EF4444', fontSize: 'var(--fs-sm)' }}>
            {message}
          </div>
        )}

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Joined</th>
                {/* <th></th> */}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>
                    Loading customers…
                  </td>
                </tr>
              )}

              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--text3)' }}>
                    No customers found.
                  </td>
                </tr>
              )}

              {!isLoading &&
                paginated.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                          style={{
                            width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-active)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--accent-hover)', flexShrink: 0,
                          }}
                        >
                          {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text1)', fontSize: 'var(--fs-sm)' }}>{u.name}</div>
                          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{u.phone}</td>
                    <td>
                      <span className={`badge ${u.status === 'active' ? 'badge-active' : 'badge-pending'}`}>
                        {u.status === 'active' ? 'verified' : 'not verified'}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>{u.joined}</td>
                    {/* <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-icon" onClick={() => openEdit(u)} aria-label="Edit">
                          <Pencil size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon"
                          onClick={() => { setActive(u); setDeleteOpen(true) }}
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td> */}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!isLoading && filtered.length > 0 && (
          <Pagination
            total={filtered.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* ---------- add / edit modal (disabled) ----------
      <Modal
        open={modalOpen}
        title={active ? 'Edit Customer' : 'Add Customer'}
        subtitle="Create or update customer account details"
        onClose={closeModal}
        footer={
          <>
            <button className="btn btn-outline" onClick={closeModal} disabled={isSaving}>Cancel</button>
            <button className="btn btn-primary" onClick={saveUser} disabled={isSaving}>
              {isSaving ? 'Saving…' : active ? 'Update Customer' : 'Create Customer'}
            </button>
          </>
        }
      >
        {formErr && (
          <div
            style={{
              background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
              color: '#EF4444', fontSize: 'var(--fs-sm)', padding: '8px 12px',
              borderRadius: 8, marginBottom: 'var(--space-md)',
            }}
          >
            {formErr}
          </div>
        )}

        <div className="form-grid cols-2">
          <div className="form-field">
            <label>First Name</label>
            <input
              className="input"
              placeholder="Enter first name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
          </div>
          <div className="form-field">
            <label>Last Name</label>
            <input
              className="input"
              placeholder="Enter last name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </div>
          <div className="form-field">
            <label>Email Address</label>
            <input
              className="input"
              type="email"
              placeholder="Enter email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="form-field">
            <label>Phone Number</label>
            <input
              className="input"
              placeholder="10-digit phone number"
              maxLength={10}
              inputMode="numeric"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
            />
          </div>
          <div className="form-field full">
            <label>{active ? 'New Password (optional)' : 'Password'}</label>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              placeholder={active ? 'Leave blank to keep current password' : 'Minimum 8 characters'}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete customer"
        message={`Remove ${active?.name || 'this customer'} from the system?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />
      */}

      <style>{`
        .dash-page{display:flex;flex-direction:column;gap:var(--space-lg);width:100%;}

        /* toolbar */
        .cust-toolbar{
          display:flex; align-items:center; gap:var(--space-sm);
          padding:var(--space-md) var(--space-lg);
          border-bottom:1px solid var(--border);
          flex-wrap:wrap;
        }
        .cust-search{ position:relative; flex:1; min-width:220px; }
        .cust-search-icon{
          position:absolute; left:10px; top:50%;
          transform:translateY(-50%); color:var(--text3); pointer-events:none;
        }

        /* segmented filter */
        .seg-filter{
          display:inline-flex; align-items:center; gap:2px;
          padding:3px;
          border:1px solid var(--border);
          border-radius:10px;
          background:var(--bg-card, transparent);
        }
        .seg-btn{
          display:inline-flex; align-items:center; gap:6px;
          border:1px solid transparent;
          background:transparent;
          color:var(--text3);
          font-size:var(--fs-xs, 12px);
          font-weight:500;
          padding:6px 14px;
          border-radius:8px;
          cursor:pointer;
          white-space:nowrap;
          transition:all .18s ease;
        }
        .seg-btn:hover{ color:var(--text1); }
        .seg-btn.active{
          background:var(--bg-active, rgba(79,70,229,0.12));
          color:var(--text1);
          border-color:var(--border);
          box-shadow:0 1px 3px rgba(0,0,0,0.12);
          font-weight:600;
        }
        .seg-count{
          font-size:10px;
          font-weight:600;
          min-width:18px;
          padding:1px 6px;
          border-radius:999px;
          background:rgba(128,128,128,0.15);
          color:inherit;
          text-align:center;
        }
        .seg-btn.active .seg-count{
          background:var(--accent, #4F46E5);
          color:#fff;
        }

        @media (max-width: 640px){
          .seg-filter{ width:100%; overflow-x:auto; }
          .seg-btn{ flex:1; justify-content:center; }
        }
      `}</style>
    </div>
  )
}